/**
 * usePlans Hook
 *
 * React hook for fetching available subscription plans.
 * Centralizes plan fetching logic used across billing components.
 *
 * @example
 * Basic usage:
 * ```tsx
 * const { plans, isLoading, error } = usePlans();
 * ```
 *
 * @example
 * Conditional fetching:
 * ```tsx
 * const { plans, isLoading } = usePlans({ enabled: dialogOpen });
 * ```
 *
 * @example
 * Filter plans:
 * ```tsx
 * const { plans } = usePlans();
 * const paidPlans = plans?.filter(plan => !plan.is_contact_sales);
 * ```
 */

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

/**
 * Plan API response structure
 * Represents a subscription plan from the API
 */
export interface PlanApiResponse {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  monthly_price_cents: number | null;
  yearly_price_cents: number | null;
  storage_limit_mb: number | null;
  max_tables: number | null;
  max_team_members: number | null;
  spreadsheet_pulls_limit: number | null;
  is_active: boolean;
  is_public: boolean;
  is_contact_sales: boolean;
  is_recommended: boolean;
  sort_order: number;
}

interface UsePlansOptions {
  /**
   * Enable/disable the query
   * @default true
   */
  enabled?: boolean;
}

interface UsePlansResult {
  /**
   * Array of available plans
   */
  plans: PlanApiResponse[] | undefined;
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * Error state
   */
  error: Error | null;
}

/**
 * Hook for fetching available subscription plans
 *
 * Fetches all plans from the API and caches them using TanStack Query.
 * Plans are automatically refetched when stale.
 *
 * @param options - Optional configuration
 * @returns Plans data, loading state, and error state
 */
export function usePlans(options: UsePlansOptions = {}): UsePlansResult {
  const { enabled = true } = options;
  const rpc = useRpc();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: async () => {
      const res = await rpc.api.private.plans.$get();
      if (!res.ok) {
        throw new Error('Failed to fetch plans');
      }
      // Plans response: { plans: [...] } - NOT { success, data }
      const data = await res.json();
      return data.plans;
    },
    enabled,
  });

  return {
    plans: data,
    isLoading,
    error: error as Error | null,
  };
}
